import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import { useObserver } from 'mobx-react-lite'
import { FC } from 'react'
import { scrollToError } from 'service/scrollToError'
import { useApi } from 'service/useApi'
import { useAuth } from 'service/useAuth'
import { useDebouncedCallback } from 'service/useDebounce'
import { UnleashFeatureFlagKeys, useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'
import { useI18n } from 'utils/localisation'

import { NetworkAutocomplete } from '../NetworkAutocomplete/NetworkAutocomplete'
import { AutocompleteResult } from '../NetworkAutocomplete/NetworkAutocompleteOption'
import { IOrganizationJSON, IOrganizationResJSON } from '../Organizations/OrganizationsModel'
import { IVATResponseJSON } from '../Vat'
import { BillingAddress } from './BillingAddress'
import { salutationsToTermMap, salutationValues } from './BillingContactModel'
import { BillingEntity } from './BillingEntity'
import { DefaultForIntegrations } from './DefaultForIntegrations'
import { TLegalEntity } from './LegalEntitiesModel'
import { LegalEntityInfo } from './LegalEntityInfoBox'
import { LegalEntityVatValidator } from './LegalEntityVatValidator'

const useStyles = makeStyles<Theme>((theme) =>
  createStyles({
    dialogPaper: {
      maxWidth: 720,
    },
    dialogContent: {
      minHeight: 485,
    },
    autocompleteWrapper: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    autocompleteWrapperContact: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
    autocompleteLabel: {
      flex: 1,
      fontSize: theme.typography.body1.fontSize,
    },
    autocomplete: {
      flex: 3.9,
    },
    contactWrapper: {
      paddingTop: theme.spacing(2),
    },
  })
)

export interface LegalEntityDialogProps {
  open: boolean
  handleClose: () => void
  legalEntity: TLegalEntity
  onClickSave: (legalEntity: TLegalEntity) => void
  searchOrganizations: (searchText: string) => Promise<IOrganizationResJSON>
  organization?: Partial<IOrganizationJSON>
  validateVat: (vatId: string) => Promise<IVATResponseJSON>
}

export const formatOrganizationToCompletion = (organization: IOrganizationJSON): AutocompleteResult => {
  if (!organization) {
    // @ts-expect-error TS(2322) FIXME: Type 'undefined' is not assignable to type 'Autoco... Remove this comment to see the full error message
    return
  }
  return { label: organization.name, value: organization._id }
}

export const LegalEntityDialog: FC<LegalEntityDialogProps> = ({
  searchOrganizations,
  open,
  handleClose,
  legalEntity,
  organization,
  validateVat,
  onClickSave,
}) => {
  const { translate } = useI18n()
  const { isEnabled } = useUnleashFeatureFlags()
  const {
    state: { loggedInAsCustomer },
  } = useAuth()
  const classes = useStyles()
  const theme = useTheme()
  const { network } = useApi()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [save] = useDebouncedCallback(onClickSave, 500) // prevent multiple saves

  // TODO: Remove this as part of https://forto.atlassian.net/browse/CMD2-182 once new LE Creation flow is live
  if (!loggedInAsCustomer && !legalEntity._id) {
    legalEntity.billingEntityId.setValue('62028a945298c26a2093207a')
  }

  const getCompletions = (searchText: string): Promise<AutocompleteResult[]> => {
    return searchOrganizations(searchText).then((results) => results.data.map(formatOrganizationToCompletion))
  }

  const handleSave = () => {
    legalEntity.setTriedSubmit(true)
    legalEntity.validate()
    scrollToError()
    if (legalEntity.isChangedAndValid) {
      save(legalEntity)
    }
  }

  return useObserver(() => (
    <Dialog fullScreen={fullScreen} open={open} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle onClose={handleClose} showClose>
        {translate('directory-legal_entity_title')}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.autocompleteWrapper}>
          <Typography className={classes.autocompleteLabel}>{translate('directory-organization_title')}</Typography>
          <div className={classes.autocomplete}>
            {!organization && (
              <NetworkAutocomplete
                getCompletions={getCompletions}
                handleSelection={(selection) => legalEntity.setOrganizationId(selection.value.toString())}
              />
            )}
            <span>{organization && organization.name}</span>
          </div>
        </div>
        <LegalEntityVatValidator legalEntity={legalEntity} validateVat={validateVat} />
        {(!legalEntity.hasVat || legalEntity.hasValidVat || legalEntity.region !== 'EU') && (
          <Grid container spacing={2}>
            <Grid xs={12}>
              <TextField
                fullWidth
                label={translate('directory-legal_entity_company_name')}
                value={legalEntity.name.value}
                onChange={(e) => legalEntity.setName(e.target.value)}
                helperText={legalEntity.validateName().isInvalid && translate(legalEntity.validateName().message)}
                error={legalEntity.validateName().isInvalid}
                disabled={legalEntity.name.disabled}
                required
              />
            </Grid>
            {loggedInAsCustomer && (
              <BillingEntity
                legalEntity={legalEntity}
                helperText={
                  legalEntity.validateBillingEntityId().isInvalid &&
                  translate(legalEntity.validateBillingEntityId().message)
                }
                error={legalEntity.validateBillingEntityId().isInvalid}
                getBillingEntities={network.billingEntities.getBillingEntities}
              />
            )}
            <BillingAddress legalEntity={legalEntity} />
            <Grid xs={2} className={classes.contactWrapper}>
              <TextField
                select
                fullWidth
                label={translate('directory-form_salutation')}
                value={legalEntity.billingContact.salutation.value}
                onChange={(e) => legalEntity.billingContact.setSalutation(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                {salutationValues.map((salutation) => (
                  <MenuItem key={salutation} value={salutation}>
                    {translate(salutationsToTermMap[salutation])}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid xs={5}>
              <TextField
                fullWidth
                label={translate('directory-form_first_name')}
                value={legalEntity.billingContact.firstName.value}
                onChange={(e) => legalEntity.billingContact.setFirstName(e.target.value)}
                helperText={
                  legalEntity.billingContact.validateFirstName().isInvalid &&
                  translate(legalEntity.billingContact.validateFirstName().message)
                }
                error={legalEntity.billingContact.validateFirstName().isInvalid}
              />
            </Grid>
            <Grid xs={5}>
              <TextField
                fullWidth
                label={translate('directory-form_last_name')}
                value={legalEntity.billingContact.lastName.value}
                onChange={(e) => legalEntity.billingContact.setLastName(e.target.value)}
                helperText={
                  legalEntity.billingContact.validateLastName().isInvalid &&
                  translate(legalEntity.billingContact.validateLastName().message)
                }
                error={legalEntity.billingContact.validateLastName().isInvalid}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label={translate('directory-form_eori')}
                value={legalEntity.eoriNumber.value}
                onChange={(e) => legalEntity.setEori(e.target.value)}
                helperText={legalEntity.validateEori().isInvalid && translate(legalEntity.validateEori().message)}
                error={legalEntity.validateEori().isInvalid}
                required
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label={translate('directory-form_notes')}
                value={legalEntity.notes.value}
                onChange={(e) => legalEntity.setNotes(e.target.value)}
                helperText={legalEntity.validateNotes().isInvalid && translate(legalEntity.validateNotes().message)}
                error={legalEntity.validateNotes().isInvalid}
              />
            </Grid>
            {loggedInAsCustomer && isEnabled(UnleashFeatureFlagKeys.SHOW_DEFAULT_FOR_INTEGRATIONS_IN_LEGAL_ENTITY) && (
              <DefaultForIntegrations legalEntity={legalEntity} />
            )}
            {!legalEntity._id && (
              <Grid xs={12}>
                <LegalEntityInfo />
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{translate('directory-button_cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          data-userlane-id="modal-create-legal-button-save"
          disabled={legalEntity.isEU && !legalEntity.hasValidVat}
        >
          {translate('directory-button_request')}
        </Button>
      </DialogActions>
    </Dialog>
  ))
}
